import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Edit as EditIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_EMAIL_TEMPLATES,
  CREATE_EMAIL_TEMPLATE,
  UPDATE_EMAIL_TEMPLATE,
  GET_EMAIL_VARIABLES,
} from "../graphql/queries";

const TEMPLATE_TYPES = [
  { value: "coupon_success", label: "Coupon Success Email" },
  { value: "admin_notification", label: "Admin Notification" },
  { value: "attendee_welcome", label: "Attendee Welcome Email" },
];

// Will be loaded dynamically from backend
const DEFAULT_VARIABLES = [
  "$Name",
  "$FirstName",
  "$Email",
  "$CouponNo",
  "$TotalBill",
  "$DiscountApplied",
  "$GrandTotal",
  "$ShopName",
  "$ShopLocation",
  "$ShopContact",
  "$shop_password",
  "$ScannedBy",
];

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateType, setTemplateType] = useState("coupon_success");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewBody, setPreviewBody] = useState("");
  const [useHtmlEditor, setUseHtmlEditor] = useState(true);
  const [availableVariables, setAvailableVariables] = useState(DEFAULT_VARIABLES);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "info" });

  const { data, loading, refetch } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: {},
  });

  const { data: variablesData } = useQuery(GET_EMAIL_VARIABLES);

  const [createTemplate] = useMutation(CREATE_EMAIL_TEMPLATE);
  const [updateTemplate] = useMutation(UPDATE_EMAIL_TEMPLATE);

  useEffect(() => {
    if (data?.getEmailTemplates) {
      setTemplates(data.getEmailTemplates);
    }
  }, [data]);

  useEffect(() => {
    if (variablesData?.getEmailVariables) {
      // Format variables with $ prefix
      const formatted = variablesData.getEmailVariables.map((v) => `$${v}`);
      setAvailableVariables(formatted);
    }
  }, [variablesData]);

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setSubject(template.subject || "");
    setBody(template.body || "");
    setTemplateType(template.templateType);
    setEditDialog(true);
  };

  const handleCreate = (type) => {
    setSelectedTemplate(null);
    setSubject("");
    setBody("");
    setTemplateType(type);
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      if (selectedTemplate) {
        await updateTemplate({
          variables: {
            id: selectedTemplate.id,
            subject,
            body,
          },
        });
        setNotification({
          open: true,
          message: "Template updated successfully",
          severity: "success",
        });
      } else {
        await createTemplate({
          variables: {
            templateType,
            subject,
            body,
          },
        });
        setNotification({
          open: true,
          message: "Template created successfully",
          severity: "success",
        });
      }
      setEditDialog(false);
      refetch();
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to save template",
        severity: "error",
      });
    }
  };

  const handlePreview = (template) => {
    // Replace variables with sample data
    let previewSubj = template.subject || "";
    let previewBod = template.body || "";

    // Sample data for preview - comprehensive mapping
    const sampleData = {};
    
    // Add all available variables with sample values
    availableVariables.forEach((variable) => {
      const key = variable.substring(1); // Remove $ prefix
      
      // Generate sample value based on variable name
      if (key.startsWith("admin_")) {
        const field = key.replace("admin_", "");
        if (field === "name") sampleData[key] = "Admin User";
        else if (field === "email") sampleData[key] = "admin@example.com";
        else if (field === "company") sampleData[key] = "Event Company";
        else sampleData[key] = `Admin ${field}`;
      } else if (key.startsWith("shop_")) {
        const field = key.replace("shop_", "");
        if (field === "name") sampleData[key] = "Sample Shop";
        else if (field === "location") sampleData[key] = "Sample Location";
        else if (field === "contact") sampleData[key] = "+968 1234 5678";
        else if (field === "password") sampleData[key] = "password123";
        else sampleData[key] = `Shop ${field}`;
      } else if (key.startsWith("user_")) {
        const field = key.replace("user_", "");
        if (field.toLowerCase().includes("name") || field === "Name") {
          sampleData[key] = "John Doe";
        } else if (field.toLowerCase().includes("first")) {
          sampleData[key] = "John";
        } else if (field.toLowerCase().includes("last")) {
          sampleData[key] = "Doe";
        } else if (field.toLowerCase().includes("email")) {
          sampleData[key] = "john.doe@example.com";
        } else if (field.toLowerCase().includes("mobile") || field.toLowerCase().includes("phone")) {
          sampleData[key] = "+968 12345678";
        } else if (field.toLowerCase().includes("title")) {
          sampleData[key] = "Mr";
        } else if (field.toLowerCase().includes("company")) {
          sampleData[key] = "Example Corp";
        } else {
          sampleData[key] = `[${field}]`;
        }
      } else {
        // Common variables
        if (key === "Name" || key === "name") sampleData[key] = "John Doe";
        else if (key === "FirstName" || key === "First Name" || key === "FirstName") sampleData[key] = "John";
        else if (key === "LastName" || key === "Last Name") sampleData[key] = "Doe";
        else if (key === "Email" || key === "email") sampleData[key] = "john.doe@example.com";
        else if (key === "CouponNo") sampleData[key] = "JOTOMBE2ND";
        else if (key === "TotalBill") sampleData[key] = "250.00";
        else if (key === "DiscountApplied") sampleData[key] = "25.00";
        else if (key === "GrandTotal") sampleData[key] = "225.00";
        else if (key === "ScannedBy") sampleData[key] = "Admin User";
        else sampleData[key] = `[${key}]`;
      }
      
      // Also add variations (with/without spaces, underscores)
      const variations = [
        key.replace(/_/g, " "),
        key.replace(/\s+/g, "_"),
        key.replace(/\s+/g, ""),
      ];
      variations.forEach((variation) => {
        if (variation !== key && !sampleData[variation]) {
          sampleData[variation] = sampleData[key];
        }
      });
    });

    // Replace all variables
    availableVariables.forEach((variable) => {
      const key = variable.substring(1); // Remove $ prefix
      const value = sampleData[key] || sampleData[key.replace(/_/g, " ")] || sampleData[key.replace(/\s+/g, "_")] || `[${key}]`;
      // Escape special regex characters
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`\\$${escapedKey}\\b`, "g");
      previewSubj = previewSubj.replace(regex, value);
      previewBod = previewBod.replace(regex, value);
    });

    setPreviewSubject(previewSubj);
    setPreviewBody(previewBod);
    setPreviewDialog(true);
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById("body-textarea");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = body;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setBody(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading templates...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Email Templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage email templates for coupon success, admin notifications, and attendee welcome emails.
      </Typography>

      <Grid container spacing={3}>
        {TEMPLATE_TYPES.map((type) => {
          const template = templates.find((t) => t.templateType === type.value);
          return (
            <Grid item xs={12} md={6} lg={4} key={type.value}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{type.label}</Typography>
                    {template && <Chip label="Saved" color="success" size="small" />}
                  </Box>
                  {template ? (
                    <>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Subject: {template.subject?.substring(0, 50)}...
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated: {new Date(template.updated).toLocaleDateString()}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No template created yet
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  {template ? (
                    <>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(template)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={<PreviewIcon />}
                        onClick={() => handlePreview(template)}
                      >
                        Preview
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleCreate(type.value)}
                      variant="contained"
                    >
                      Create Template
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTemplate ? "Edit Email Template" : "Create Email Template"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Available Variables:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, maxHeight: 200, overflowY: "auto" }}>
              {availableVariables.map((variable) => (
                <Chip
                  key={variable}
                  label={variable}
                  size="small"
                  onClick={() => insertVariable(variable)}
                  sx={{ cursor: "pointer" }}
                />
              ))}
            </Box>

            <TextField
              fullWidth
              label="Email Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              margin="normal"
              placeholder="e.g., Your Coupon Claim Confirmation - $CouponNo"
            />

            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useHtmlEditor}
                    onChange={(e) => setUseHtmlEditor(e.target.checked)}
                  />
                }
                label="Use HTML Editor"
              />
            </Box>

            {useHtmlEditor ? (
              <TextField
                id="body-textarea"
                fullWidth
                label="Email Body (HTML)"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                margin="normal"
                multiline
                rows={12}
                placeholder="<h1>Hello $FirstName,</h1><p>Your coupon $CouponNo has been claimed...</p>"
                helperText="Enter HTML code. Variables like $Name, $CouponNo will be replaced when sending."
              />
            ) : (
              <Box>
                <TextField
                  id="body-textarea"
                  fullWidth
                  label="Email Body (Rich Text)"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  margin="normal"
                  multiline
                  rows={12}
                  placeholder="Hello $FirstName,\n\nYour coupon $CouponNo has been claimed..."
                  helperText="Enter plain text or simple formatting. Variables like $Name, $CouponNo will be replaced when sending."
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Note: For rich HTML formatting, enable HTML Editor above.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Subject:
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography>{previewSubject}</Typography>
            </Paper>
            <Typography variant="subtitle2" gutterBottom>
              Body:
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <div dangerouslySetInnerHTML={{ __html: previewBody }} />
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      {notification.open && (
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
          sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
        >
          {notification.message}
        </Alert>
      )}
    </Box>
  );
};

export default EmailTemplatesPage;
